// The caller's server-resolved scope tier (GET /v1/me `scope_tier`), shared by
// every surface that must distinguish "what I may reach" from "what I hold a
// permission cell for". The two are different questions: every employee holds
// hris.read / leave.read for their OWN rows, so a capability cell cannot say
// whether a list is a headcount or a mirror.
//
// It lives here rather than in LeftRail (its first caller) because the second
// caller — the dashboard's headcount KPI — must share the SAME query key. A
// second key would mean a second /v1/me round trip and two answers that can
// disagree mid-session.
//
// Deliberately NOT the ["me","identity"] key the feature-level hooks use: each
// of those narrows /v1/me to its own slice (employee_id + roles) and caches the
// narrowed object, so sharing the key would hand this gate a payload with no
// scope_tier on it and silently resolve to undefined.

import { useQuery } from "@tanstack/react-query";

import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";
import { platformGraph } from "@/api/platform-graph";

/** The server-resolved scope tiers, lowest reach first. */
export const SCOPE_TIER_ORDER = [
  "employee_self",
  "manager_subtree",
  "executive_broad",
] as const;
export type ScopeTier = (typeof SCOPE_TIER_ORDER)[number];

/** The slice of /v1/me this hook reads. */
interface MeScope {
  readonly scope_tier: ScopeTier;
}

type MeGet = (path: "/v1/me") => Promise<EnvelopedFetchResult<MeScope>>;

async function fetchScopeTier(): Promise<MeScope> {
  const get = platformGraph.GET as unknown as MeGet;
  const data = await unwrapEnvelope(get("/v1/me"));
  return { scope_tier: data.scope_tier };
}

/**
 * The caller's resolved tier, or `undefined` while /v1/me is in flight or has
 * failed. Callers treat `undefined` as DEFAULT-CLOSED — rank it below every
 * requirement so a tier-gated affordance never flashes before authority lands.
 */
export function useScopeTier(): ScopeTier | undefined {
  return useScopeTierState().tier;
}

export interface ScopeTierState {
  readonly tier: ScopeTier | undefined;
  /**
   * True only while the FIRST fetch is outstanding. A failed fetch settles to
   * false with an undefined tier, so a caller that gates on it denies rather
   * than spinning forever.
   */
  readonly isLoading: boolean;
  /** The fetch failed — "we could not ask", which is not "the answer is no". */
  readonly isError: boolean;
}

/**
 * The same shared query as {@link useScopeTier}, for callers that must tell
 * "not resolved yet" apart from "resolved to nothing" — a route guard renders
 * a loading state for the first and a denial for the second.
 */
export function useScopeTierState(): ScopeTierState {
  const query = useQuery({
    queryKey: ["me", "scope-tier"],
    queryFn: fetchScopeTier,
    staleTime: 60_000,
  });
  return { tier: query.data?.scope_tier, isLoading: query.isLoading, isError: query.isError };
}

/** Rank for tier comparisons; −1 for an unresolved tier (default-closed). */
export function scopeTierRank(tier: ScopeTier | undefined): number {
  return tier === undefined ? -1 : SCOPE_TIER_ORDER.indexOf(tier);
}
